import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload, Image as ImageIcon, Trash2, Plus, Save, GripVertical, ExternalLink, Calendar, Clock, Crop, Move, ZoomIn, Monitor, Smartphone } from "lucide-react";

/**
 * SPONSOR BANNERS PANEL — Admin Settings
 * - Upload up to 10 sponsor images
 * - Each: image, title (badge), link (opsiyonel), enabled toggle, order
 * - Zamanlanmış: start_at / end_at (opsiyonel) — süresi dolan otomatik pasif
 * - Rotation interval in seconds (2–60)
 * - Saves to PATCH /settings → sponsor_banners + banner_rotation_seconds
 */
const MAX_BANNERS = 10;

// ISO (UTC) <-> local datetime-local input dönüştürücüler
const isoToLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const localToIso = (local) => {
  if (!local) return null;
  const d = new Date(local);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
};

// Her banner için zaman penceresi durumu
const scheduleStatus = (b) => {
  const now = Date.now();
  const s = b.start_at ? new Date(b.start_at).getTime() : null;
  const e = b.end_at ? new Date(b.end_at).getTime() : null;
  if (b.enabled === false) return { key: "disabled", label: "⏸ Pasif", cls: "bg-[#1B2A22]/40 text-white" };
  if (e && now > e) return { key: "expired", label: "⏱ Süresi doldu", cls: "bg-red-600 text-white" };
  if (s && now < s) {
    const diffH = Math.ceil((s - now) / 3600000);
    return { key: "scheduled", label: `📅 ${diffH < 24 ? diffH + "sa" : Math.ceil(diffH / 24) + "g"} sonra başlar`, cls: "bg-[#B4711C] text-white" };
  }
  if (e) {
    const diffH = Math.ceil((e - now) / 3600000);
    return { key: "ending", label: `⏳ ${diffH < 24 ? diffH + "sa" : Math.ceil(diffH / 24) + "g"} kaldı`, cls: "bg-[#D9653B] text-white" };
  }
  return { key: "live", label: "● Yayında", cls: "bg-[#2E5A36] text-white" };
};

export default function SponsorBannersPanel({ settings, onSaved }) {
  const [banners, setBanners] = useState([]);
  const [rotation, setRotation] = useState(6);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [fitOpen, setFitOpen] = useState(null); // genişletilen satır index'i
  const fileRef = useRef(null);

  useEffect(() => {
    if (!settings) return;
    setBanners(Array.isArray(settings.sponsor_banners) ? [...settings.sponsor_banners] : []);
    setRotation(Number(settings.banner_rotation_seconds) || 6);
    setDirty(false);
  }, [settings]);

  const markDirty = () => setDirty(true);

  const addFromUrl = () => {
    if (banners.length >= MAX_BANNERS) {
      toast.error(`En fazla ${MAX_BANNERS} sponsor görseli ekleyebilirsin`);
      return;
    }
    setBanners((b) => [
      ...b,
      {
        id: `tmp_${Date.now()}`,
        image_url: "",
        title: "SPONSOR",
        link: "",
        enabled: true,
        sort_order: b.length,
        start_at: null,
        end_at: null,
        fit: "cover",
        focal_x: 50,
        focal_y: 50,
        zoom: 1,
        bg_color: "#1B2A22",
      },
    ]);
    markDirty();
    setFitOpen(banners.length);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (banners.length >= MAX_BANNERS) {
      toast.error(`En fazla ${MAX_BANNERS} sponsor görseli ekleyebilirsin`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = r.data?.url || "";
      if (!url) throw new Error("URL alınamadı");
      const newIdx = banners.length;
      setBanners((b) => [
        ...b,
        {
          id: `tmp_${Date.now()}`,
          image_url: url,
          title: "SPONSOR",
          link: "",
          enabled: true,
          sort_order: b.length,
          start_at: null,
          end_at: null,
          fit: "cover",
          focal_x: 50,
          focal_y: 50,
          zoom: 1,
          bg_color: "#1B2A22",
        },
      ]);
      markDirty();
      setFitOpen(newIdx);
      toast.success("✓ Görsel yüklendi — oturtma ayarları açıldı, Kaydet'i unutma");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Yüklenemedi");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const updateBanner = (idx, patch) => {
    setBanners((b) => b.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
    markDirty();
  };

  const removeBanner = (idx) => {
    if (!window.confirm("Bu sponsor görseli silinsin mi?")) return;
    setBanners((b) => b.filter((_, i) => i !== idx));
    markDirty();
  };

  const move = (idx, dir) => {
    setBanners((b) => {
      const next = [...b];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return b;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next.map((x, i) => ({ ...x, sort_order: i }));
    });
    markDirty();
  };

  const save = async () => {
    // validate
    const cleaned = banners
      .filter((b) => b.image_url && b.image_url.trim())
      .map((b, i) => {
        // start_at > end_at kontrolü — hatayı önle
        const s = b.start_at ? new Date(b.start_at).getTime() : null;
        const e = b.end_at ? new Date(b.end_at).getTime() : null;
        if (s && e && s > e) {
          throw new Error(`Satır ${i + 1}: Başlangıç tarihi bitiş tarihinden sonra olamaz`);
        }
        return {
          id: b.id || `b_${Date.now()}_${i}`,
          image_url: b.image_url.trim(),
          title: (b.title || "").trim(),
          link: (b.link || "").trim(),
          enabled: b.enabled !== false,
          sort_order: i,
          start_at: b.start_at || null,
          end_at: b.end_at || null,
          fit: b.fit === "contain" ? "contain" : "cover",
          focal_x: Math.max(0, Math.min(100, Number(b.focal_x ?? 50))),
          focal_y: Math.max(0, Math.min(100, Number(b.focal_y ?? 50))),
          zoom: Math.max(1, Math.min(3, Number(b.zoom ?? 1))),
          bg_color: (b.bg_color || "#1B2A22").trim(),
        };
      });
    const rot = Math.max(2, Math.min(60, parseInt(rotation, 10) || 6));
    setSaving(true);
    try {
      await api.patch("/settings", {
        sponsor_banners: cleaned,
        banner_rotation_seconds: rot,
      });
      toast.success("✓ Sponsor reklamları kaydedildi");
      setDirty(false);
      if (onSaved) await onSaved();
    } catch (e) {
      toast.error(e?.message || e?.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const activeCount = banners.filter((b) => b.enabled !== false && b.image_url).length;

  return (
    <div className="bg-white border border-[#E8E2D5] rounded-2xl p-5 space-y-4" data-testid="sponsor-banners-panel">
      <div>
        <h3 className="font-heading font-bold text-lg flex items-center gap-2">
          <ImageIcon className="w-5 h-5 text-[#D9653B]" /> Sponsor Reklamları
        </h3>
        <p className="text-xs text-[#1B2A22]/60 mt-1">
          Müşteri ana sayfasındaki hoş geldiniz alanı yerine döngüsel sponsor reklamları gösterir.
          En fazla <b>{MAX_BANNERS}</b> görsel eklenebilir. Hiç görsel yoksa normal "HOŞ GELDİNİZ" bölümü görünür.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <Label className="text-xs">Döngü Süresi (saniye)</Label>
          <Input
            data-testid="banner-rotation-seconds"
            type="number"
            min="2"
            max="60"
            value={rotation}
            onChange={(e) => { setRotation(e.target.value); markDirty(); }}
            className="bg-[#FAF7F2] font-mono"
          />
          <p className="text-[10px] text-[#1B2A22]/60 mt-1">
            2–60 saniye arası. Her görsel bu süre kadar ekranda kalır, sonra bir sonrakine geçer.
          </p>
        </div>
        <div className="flex flex-col justify-end gap-2">
          <Button
            data-testid="banner-upload-btn"
            onClick={() => fileRef.current?.click()}
            disabled={uploading || banners.length >= MAX_BANNERS}
            className="rounded-full bg-[#2E5A36] hover:bg-[#24472B] text-white font-heading font-bold"
          >
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? "Yükleniyor..." : "Görsel Yükle"}
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFile}
            data-testid="banner-file-input"
          />
          <button
            type="button"
            onClick={addFromUrl}
            disabled={banners.length >= MAX_BANNERS}
            className="text-[11px] text-[#B4711C] underline"
            data-testid="banner-add-url-btn"
          >
            + URL ile ekle
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-[#E8E2D5]">
        <p className="text-xs">
          <b>{activeCount}</b> aktif · <b>{banners.length}</b>/{MAX_BANNERS} toplam
          {dirty && <span className="ml-2 text-[#D9653B] font-bold">● Kaydedilmemiş değişiklik</span>}
        </p>
      </div>

      {banners.length === 0 ? (
        <div className="text-center py-8 bg-[#FAF7F2] rounded-xl border border-dashed border-[#E8E2D5]">
          <ImageIcon className="w-10 h-10 text-[#1B2A22]/20 mx-auto mb-2" />
          <p className="text-sm text-[#1B2A22]/60">
            Henüz sponsor görseli yok — <b>Görsel Yükle</b>'ye bas ve ilk görseli ekle.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {banners.map((b, i) => (
            <div
              key={b.id || i}
              data-testid={`banner-row-${i}`}
              className={`grid grid-cols-[auto_80px_1fr_auto] gap-3 items-center p-3 rounded-xl border ${b.enabled !== false ? "bg-white border-[#E8E2D5]" : "bg-[#1B2A22]/5 border-[#1B2A22]/10 opacity-60"}`}
            >
              <div className="flex flex-col gap-0.5">
                <button
                  type="button"
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  className="text-[#1B2A22]/50 hover:text-[#2E5A36] disabled:opacity-20 text-lg leading-none"
                  title="Yukarı"
                >
                  ▲
                </button>
                <span className="text-[9px] font-mono text-[#1B2A22]/50 text-center">{i + 1}</span>
                <button
                  type="button"
                  onClick={() => move(i, +1)}
                  disabled={i === banners.length - 1}
                  className="text-[#1B2A22]/50 hover:text-[#2E5A36] disabled:opacity-20 text-lg leading-none"
                  title="Aşağı"
                >
                  ▼
                </button>
              </div>

              <div className="w-20 h-20 bg-[#1B2A22]/5 rounded-lg overflow-hidden flex items-center justify-center">
                {b.image_url ? (
                  <img
                    src={b.image_url}
                    alt={b.title || "banner"}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                ) : (
                  <ImageIcon className="w-6 h-6 text-[#1B2A22]/30" />
                )}
              </div>

              <div className="space-y-1.5 min-w-0">
                <Input
                  data-testid={`banner-url-${i}`}
                  value={b.image_url || ""}
                  onChange={(e) => updateBanner(i, { image_url: e.target.value })}
                  placeholder="Görsel URL (/api/files/... veya /api/uploads/...)"
                  className="bg-[#FAF7F2] font-mono text-[11px] h-8"
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    data-testid={`banner-title-${i}`}
                    value={b.title || ""}
                    onChange={(e) => updateBanner(i, { title: e.target.value })}
                    placeholder="Rozet metni (örn: SPONSOR)"
                    className="bg-[#FAF7F2] text-xs h-8"
                  />
                  <Input
                    data-testid={`banner-link-${i}`}
                    value={b.link || ""}
                    onChange={(e) => updateBanner(i, { link: e.target.value })}
                    placeholder="Tıklama linki (opsiyonel)"
                    className="bg-[#FAF7F2] text-xs h-8"
                  />
                </div>
                {/* Zamanlanmış yayın — opsiyonel */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] text-[#1B2A22]/60 flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> Başlangıç
                    </Label>
                    <Input
                      type="datetime-local"
                      data-testid={`banner-start-${i}`}
                      value={isoToLocal(b.start_at)}
                      onChange={(e) => updateBanner(i, { start_at: localToIso(e.target.value) })}
                      className="bg-[#FAF7F2] text-[11px] h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] text-[#1B2A22]/60 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Bitiş
                    </Label>
                    <Input
                      type="datetime-local"
                      data-testid={`banner-end-${i}`}
                      value={isoToLocal(b.end_at)}
                      onChange={(e) => updateBanner(i, { end_at: localToIso(e.target.value) })}
                      className="bg-[#FAF7F2] text-[11px] h-8"
                    />
                  </div>
                </div>
                {(b.start_at || b.end_at) && (
                  <div className="flex items-center gap-2 text-[10px]">
                    <button
                      type="button"
                      onClick={() => updateBanner(i, { start_at: null, end_at: null })}
                      className="text-[#B4711C] underline"
                    >
                      ✕ Tarihleri temizle
                    </button>
                    {b.start_at && b.end_at && new Date(b.start_at) > new Date(b.end_at) && (
                      <span className="text-red-600 font-bold">⚠️ Başlangıç bitişten sonra!</span>
                    )}
                  </div>
                )}
                <div className="flex items-center gap-3 text-[11px] flex-wrap">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={b.enabled !== false}
                      onChange={(e) => updateBanner(i, { enabled: e.target.checked })}
                      className="w-3.5 h-3.5 accent-[#2E5A36]"
                      data-testid={`banner-enabled-${i}`}
                    />
                    <span className={b.enabled !== false ? "text-[#2E5A36] font-bold" : "text-[#1B2A22]/50"}>
                      {b.enabled !== false ? "Aktif" : "Pasif"}
                    </span>
                  </label>
                  {(() => {
                    const st = scheduleStatus(b);
                    return (
                      <span className={`text-[10px] font-heading font-bold rounded-full px-2 py-0.5 ${st.cls}`} data-testid={`banner-status-${i}`}>
                        {st.label}
                      </span>
                    );
                  })()}
                  {b.link && (
                    <a href={b.link} target="_blank" rel="noopener noreferrer" className="text-[#B4711C] hover:underline inline-flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" /> aç
                    </a>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-1 self-start">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setFitOpen((cur) => (cur === i ? null : i))}
                  className={`h-8 w-8 p-0 rounded-full ${fitOpen === i ? "bg-[#E5A024] text-white border-[#E5A024]" : "text-[#B4711C] border-[#E5A024]"}`}
                  title="Görseli alana oturt"
                  data-testid={`banner-fit-${i}`}
                  disabled={!b.image_url}
                >
                  <Crop className="w-3.5 h-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => removeBanner(i)}
                  className="h-8 w-8 p-0 rounded-full text-red-600 border-red-300 hover:bg-red-50"
                  title="Sil"
                  data-testid={`banner-remove-${i}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Oturtma editörü — seçili bannerın altında açılır */}
      {fitOpen !== null && banners[fitOpen] && banners[fitOpen].image_url && (
        <FitEditor
          banner={banners[fitOpen]}
          index={fitOpen}
          onChange={(patch) => updateBanner(fitOpen, patch)}
          onClose={() => setFitOpen(null)}
        />
      )}

      <div className="pt-3 border-t border-[#E8E2D5] flex justify-end">
        <Button
          data-testid="banner-save-btn"
          onClick={save}
          disabled={saving || !dirty}
          className="rounded-full h-11 px-6 bg-[#D9653B] hover:bg-[#B4522F] text-white font-heading font-bold"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Kaydediliyor..." : "Sponsor Reklamlarını Kaydet"}
        </Button>
      </div>
    </div>
  );
}

/**
 * FitEditor — görselin hero alanına nasıl oturacağını canlı önizlemeyle ayarlar.
 * - Kapla (cover) / Sığdır (contain) toggle
 * - Focal nokta: önizleme üstüne tıkla veya X/Y slider
 * - Zoom slider (cover modunda etkili)
 * - Arka plan rengi (contain modunda kenar boşluk)
 * - Masaüstü (32:9) ve mobil (16:9) iki ayrı canlı önizleme
 */
function FitEditor({ banner, index, onChange, onClose }) {
  const previewRef = useRef(null);
  const fit = banner.fit === "contain" ? "contain" : "cover";
  const fx = Number(banner.focal_x ?? 50);
  const fy = Number(banner.focal_y ?? 50);
  const zm = Math.max(1, Math.min(3, Number(banner.zoom ?? 1)));
  const bg = banner.bg_color || "#1B2A22";

  const setFromClick = (e) => {
    const r = previewRef.current?.getBoundingClientRect();
    if (!r) return;
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;
    onChange({
      focal_x: Math.max(0, Math.min(100, Math.round(x))),
      focal_y: Math.max(0, Math.min(100, Math.round(y))),
    });
  };

  const imgStyle = {
    objectFit: fit,
    objectPosition: `${fx}% ${fy}%`,
    transform: fit === "cover" && zm > 1 ? `scale(${zm})` : undefined,
    transformOrigin: `${fx}% ${fy}%`,
    background: bg,
    width: "100%",
    height: "100%",
    display: "block",
  };

  return (
    <div
      className="bg-[#FAF7F2] border-2 border-[#E5A024] rounded-2xl p-4 space-y-3 mt-2"
      data-testid={`fit-editor-${index}`}
    >
      <div className="flex items-center justify-between">
        <h4 className="font-heading font-bold text-sm text-[#1B2A22] flex items-center gap-2">
          <Crop className="w-4 h-4 text-[#E5A024]" />
          Görseli Alana Oturt — {index + 1}. sponsor
        </h4>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-[#1B2A22]/60 hover:text-[#1B2A22] underline"
          data-testid={`fit-editor-close-${index}`}
        >
          ✕ kapat
        </button>
      </div>

      {/* Önizlemeler — gerçek hero oranları */}
      <div className="grid gap-3">
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-[11px] font-heading font-bold text-[#1B2A22] flex items-center gap-1.5">
              <Monitor className="w-3 h-3" /> Masaüstü (32:9)
            </Label>
            <span className="text-[10px] text-[#1B2A22]/50 font-mono">önizleme üstüne tıkla → odak</span>
          </div>
          <div
            ref={previewRef}
            onClick={setFromClick}
            className="relative w-full overflow-hidden rounded-lg border border-[#E8E2D5] cursor-crosshair"
            style={{ aspectRatio: "32 / 9", background: bg }}
            data-testid={`fit-preview-desktop-${index}`}
          >
            <img src={banner.image_url} alt="" style={imgStyle} />
            <div
              className="absolute w-6 h-6 -ml-3 -mt-3 rounded-full border-2 border-white shadow-lg pointer-events-none"
              style={{
                left: `${fx}%`,
                top: `${fy}%`,
                background: "rgba(217,101,59,0.9)",
              }}
            />
          </div>
        </div>
        <div>
          <Label className="text-[11px] font-heading font-bold text-[#1B2A22] flex items-center gap-1.5 mb-1">
            <Smartphone className="w-3 h-3" /> Mobil (16:9)
          </Label>
          <div
            className="relative w-full max-w-xs overflow-hidden rounded-lg border border-[#E8E2D5]"
            style={{ aspectRatio: "16 / 9", background: bg }}
            data-testid={`fit-preview-mobile-${index}`}
          >
            <img src={banner.image_url} alt="" style={imgStyle} />
          </div>
        </div>
      </div>

      {/* Kontroller */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-[#E8E2D5]">
        <div>
          <Label className="text-[11px] font-heading font-bold">Oturma modu</Label>
          <div className="flex gap-1 mt-1">
            <button
              type="button"
              onClick={() => onChange({ fit: "cover" })}
              className={`flex-1 h-9 rounded-full text-xs font-heading font-bold border ${fit === "cover" ? "bg-[#2E5A36] text-white border-[#2E5A36]" : "bg-white text-[#1B2A22] border-[#E8E2D5]"}`}
              data-testid={`fit-mode-cover-${index}`}
            >
              Kapla (kırpar)
            </button>
            <button
              type="button"
              onClick={() => onChange({ fit: "contain" })}
              className={`flex-1 h-9 rounded-full text-xs font-heading font-bold border ${fit === "contain" ? "bg-[#2E5A36] text-white border-[#2E5A36]" : "bg-white text-[#1B2A22] border-[#E8E2D5]"}`}
              data-testid={`fit-mode-contain-${index}`}
            >
              Sığdır (kenar)
            </button>
          </div>
          <p className="text-[10px] text-[#1B2A22]/60 mt-1">
            {fit === "cover" ? "Alan tam dolar, taşan kısım kırpılır." : "Görsel tamamen görünür, boşluk arka plan rengi ile dolar."}
          </p>
        </div>

        <div>
          <Label className="text-[11px] font-heading font-bold flex items-center justify-between">
            <span className="flex items-center gap-1"><ZoomIn className="w-3 h-3" /> Yakınlaştırma</span>
            <span className="font-mono text-[10px] text-[#1B2A22]/60">{zm.toFixed(2)}x</span>
          </Label>
          <input
            type="range"
            min="1"
            max="3"
            step="0.05"
            value={zm}
            onChange={(e) => onChange({ zoom: parseFloat(e.target.value) })}
            disabled={fit !== "cover"}
            className="w-full accent-[#2E5A36] disabled:opacity-40"
            data-testid={`fit-zoom-${index}`}
          />
          <p className="text-[10px] text-[#1B2A22]/60">
            {fit === "cover" ? "Odak noktası etrafında yakınlaştır." : "Sığdır modunda yakınlaştırma kapalı."}
          </p>
        </div>

        <div>
          <Label className="text-[11px] font-heading font-bold flex items-center justify-between">
            <span>Yatay odak (X)</span>
            <span className="font-mono text-[10px] text-[#1B2A22]/60">{Math.round(fx)}%</span>
          </Label>
          <input
            type="range"
            min="0"
            max="100"
            step="1"
            value={fx}
            onChange={(e) => onChange({ focal_x: parseFloat(e.target.value) })}
            className="w-full accent-[#2E5A36]"
            data-testid={`fit-focal-x-${index}`}
          />
        </div>

        <div>
          <Label className="text-[11px] font-heading font-bold flex items-center justify-between">
            <span>Dikey odak (Y)</span>
            <span className="font-mono text-[10px] text-[#1B2A22]/60">{Math.round(fy)}%</span>
          </Label>
          <input
            type="range"
            min="0"
            max="100"
            step="1"
            value={fy}
            onChange={(e) => onChange({ focal_y: parseFloat(e.target.value) })}
            className="w-full accent-[#2E5A36]"
            data-testid={`fit-focal-y-${index}`}
          />
        </div>

        <div className="md:col-span-2 flex items-center gap-3">
          <Label className="text-[11px] font-heading font-bold">Arka plan rengi (sığdır modunda)</Label>
          <input
            type="color"
            value={bg}
            onChange={(e) => onChange({ bg_color: e.target.value })}
            className="h-9 w-16 rounded-lg border border-[#E8E2D5] cursor-pointer"
            data-testid={`fit-bgcolor-${index}`}
          />
          <code className="text-[11px] font-mono text-[#1B2A22]/70">{bg}</code>
          <button
            type="button"
            onClick={() => onChange({ focal_x: 50, focal_y: 50, zoom: 1, fit: "cover", bg_color: "#1B2A22" })}
            className="ml-auto text-[11px] text-[#B4711C] underline"
            data-testid={`fit-reset-${index}`}
          >
            ↺ Varsayılana döndür
          </button>
        </div>
      </div>
    </div>
  );
}
